import { AppLauncher } from '@capacitor/app-launcher'
import { Capacitor } from '@capacitor/core'
import { compact, uniq } from 'lodash'

/**
 * Map-navigation logic, ported 1:1 from the v1 crew app (`helpers/navigate/`).
 * Builds deep links for the four supported apps and opens them in the native app
 * on device (or a new tab on web). Multi-point routes are supported where the
 * app allows it (Google up to 5 stops, Apple 2, Waze 1, Hammer 5).
 */

export type NavAddress = {
  street?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  lat?: number | null
  lng?: number | null
}

export type NavigationApp = 'apple' | 'google' | 'hammer' | 'waze'

type NavigateFn = (addresses: NavAddress[]) => string | null

type NavigationRegister = {
  title: string
  fn: NavigateFn
  available?: () => boolean
}

const formatNavAddress = (a: NavAddress): string =>
  compact([a.street, a.city, a.state, a.zip]).join(', ')

/** Each address → "lat,lng" when coordinates exist, else its formatted string. */
const toPoints = (addresses: NavAddress[]): string[] =>
  uniq(
    addresses.map((a) =>
      a.lat != null && a.lng != null
        ? `${a.lat},${a.lng}`
        : formatNavAddress(a),
    ),
  )

const google: NavigateFn = (addresses) => {
  if (addresses.length === 0) return null
  const points = toPoints(addresses)
  const params: Record<string, string> = { api: '1', travelmode: 'driving' }
  const [first, ...rest] = points
  const last = rest.pop()
  if (last) {
    params.origin = first
    params.destination = last
  } else {
    params.destination = first
  }
  if (rest.length > 0) params.waypoints = rest.join('|')
  return `https://www.google.com/maps/dir/?${new URLSearchParams(params).toString()}`
}

const apple: NavigateFn = (addresses) => {
  if (addresses.length === 0) return null
  const points = toPoints(addresses)
  if (points.length > 2) return null
  const params: Record<string, string> = { dirflg: 'd' }
  const [first, last] = points
  if (last) {
    params.saddr = first
    params.daddr = last
  } else {
    params.daddr = first
  }
  return `http://maps.apple.com/?${new URLSearchParams(params).toString()}`
}

const waze: NavigateFn = (addresses) => {
  if (addresses.length === 0) return null
  const points = uniq(
    addresses.map(
      (a): Record<string, string> =>
        a.lat != null && a.lng != null
          ? { ll: `${a.lat},${a.lng}` }
          : { q: formatNavAddress(a) },
    ),
  )
  if (points.length > 1) return null
  const params: Record<string, string> = { navigate: 'yes', ...points[0] }
  return `https://waze.com/ul?${new URLSearchParams(params).toString()}`
}

const hammer: NavigateFn = (addresses) => {
  if (addresses.length === 0) return null
  const points = toPoints(addresses)
  if (points.length === 1) points.unshift('current-location')
  return `https://hammerapp.com/app/route/${points.join('/')}/`
}

export const NAVIGATORS: Record<NavigationApp, NavigationRegister> = {
  apple: {
    title: 'Apple Maps',
    fn: apple,
    available: () => /(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent),
  },
  google: { title: 'Google Maps', fn: google },
  hammer: { title: 'HAMMER', fn: hammer },
  waze: { title: 'Waze', fn: waze },
}

/** Navigators available on the current platform (Apple Maps only on Apple). */
export const navigators = (): Array<[NavigationApp, NavigationRegister]> =>
  (
    Object.entries(NAVIGATORS) as Array<[NavigationApp, NavigationRegister]>
  ).filter(([, reg]) => !reg.available || reg.available())

export const defaultNavigationApp = (): NavigationApp => 'google'

export const navigateLink = (
  addresses: NavAddress[],
  app: NavigationApp = defaultNavigationApp(),
): string | null => NAVIGATORS[app].fn(addresses)

const NAV_APP_KEY = 'crew-nav-app'

export const getNavApp = (): NavigationApp | null => {
  const v = localStorage.getItem(NAV_APP_KEY)
  return v && v in NAVIGATORS ? (v as NavigationApp) : null
}

export const setNavApp = (app: NavigationApp) =>
  localStorage.setItem(NAV_APP_KEY, app)

/** Build the deep link for `app` and open it (native map app on device). */
export const openNavigation = (addresses: NavAddress[], app: NavigationApp) => {
  const link = navigateLink(addresses, app)
  if (!link) return
  if (Capacitor.isNativePlatform()) {
    // AppLauncher hands the URL to the OS ('_system' is Cordova-only)
    void AppLauncher.openUrl({ url: link })
  } else {
    window.open(link, '_blank')
  }
}
